import alasql from 'alasql';
import {uniqueId, find} from 'lodash';
import Graph from 'node-dijkstra';
import SchemaModel from './schema';
import TableModel from './table';

export default class DatasourceModel {
    _db = null;
    _createSql = '';
    _queryCache = new Map();
    _schemasMap = {};
    _cachedJoinPaths = {};
    _joinGraph = null;

    constructor(sourceSchemaData, createSql) {
        Object.assign(this, sourceSchemaData);
        this.id = this.id || uniqueId('datasource');
        this._cachedJoinPaths = {};
        this.updateSchemas(sourceSchemaData.schemas);
        this._joinGraph = new Graph(this.join_paths);
        this._createSql = createSql;
    }

    getFirstSchema() {
        return this.schemas[0];
    }

    getSchema(schemaName) {
        return this._schemasMap[schemaName];
    }

    getTable(fullTableName) {
        const [schemaName, tableName] = fullTableName.split('.');
        const schema = this.getSchema(schemaName);
        return schema.getTable(tableName);
    }

    getColumn(fullColumnName) {
        const [schemaName, tableName, columnName] = fullColumnName.split('.');
        const table = this.getTable(TableModel.getFullName(schemaName, tableName));
        return table.getColumnByName(columnName);
    }

    getJoins(tables) {
        const serializedTables = tables.map(({name}) => name).join('-');
        if (!this._cachedJoinPaths[serializedTables]) {
            const relations = {};
            for (const [i, table1] of tables.entries()) {
                for (const table2 of tables.slice(i + 1)) {
                    let path = this._joinGraph.path(table1.joinName, table2.joinName);
                    for (let j = 0; j < path.length - 1; j += 1) {
                        const srcTableName = path[j].split('.')[1];
                        const destTableName = path[j + 1].split('.')[1];
                        const relationIndex = this.relations.findIndex(
                            ([[__, src], [___, dest]]) => (
                                (src === srcTableName && dest === destTableName) ||
                                (src === destTableName && dest === srcTableName)
                            )
                        );
                        let relation = this.relations[relationIndex];
                        if (!relations[relationIndex]) {
                            relations[relationIndex] = (
                                relation[0][1] === destTableName ?
                                    relation.reverse() :
                                    relation
                            );
                        }
                    }
                }
            }
            const joins = Object.values(relations).map(([
                [srcSchemaName, srcTableName, srcColumnName],
                [destSchemaName, destTableName, destColumnName],
            ]) => ({
                from: find(find(this._schemasMap[srcSchemaName].tables, {name: srcTableName}).columns, {name: srcColumnName}),
                to: find(find(this._schemasMap[destSchemaName].tables, {name: destTableName}).columns, {name: destColumnName})
            }));
            const tablesLeftToCheck = [tables[0]];
            const tablesChecked = [];
            const orderedJoins = [];
            while (tablesLeftToCheck.length) {
                const tableToCheck = tablesLeftToCheck.shift();
                for (const join of joins) {
                    if (join.from.table.name === tableToCheck.name) {
                        orderedJoins.push(join);
                        if (!tablesChecked.includes(join.to.table.name)) {
                            tablesLeftToCheck.push(join.to.table);
                        }
                    }
                    tablesChecked.push(tableToCheck.name);
                }
            }
            this._cachedJoinPaths[serializedTables] = orderedJoins;
        }
        return this._cachedJoinPaths[serializedTables];
    }

    updateSchemas(schemas = this.schemas) {
        this.schemas = [];
        this._schemasMap = {};
        for (const schema of schemas) {
            const processedSchema = new SchemaModel(schema, this);
            this.schemas.push(processedSchema);
            this._schemasMap[processedSchema.name] = processedSchema;
        }
        this.schemas.sort((a, b) => a.name > b.name ? 1 : -1);
    }

    filter(searchQuery) {
        searchQuery = searchQuery.split(/[.\s()[\]{}+_-]/)
            .map(str => str.trim())
            .filter(str => str)
            .join('.*');
        const filteredDatasource = new DatasourceModel(this);
        filteredDatasource.schemas = filteredDatasource.schemas.map(schema => schema.filter(searchQuery));
        filteredDatasource.updateSchemas();
        return filteredDatasource;
    }

    canQuery() {
        return !!this._db;
    }

    executeQuery(sql) {
        if (this.canQuery() && sql) {
            if (!this._queryCache.has(sql)) {
                const start = Date.now();
                const result = this._db.exec(sql);
                const data = {
                    result,
                    executionTime: Date.now() - start,
                };
                this._queryCache.set(sql, data);
            }
            return this._queryCache.get(sql);
        }
        return null;
    }

    initDB() {
        if (this._createSql) {
            this._db = new alasql.Database();
            return new Promise((resolve) => {
                this._db.exec(this._createSql);
                resolve();
            });
        }
        return Promise.resolve();
    }

    get label() {
        return this.title_name || this.name;
    }
}
